import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { isTaskDone, taskStageName } from "../lib/taskStage";
import { tasksApi } from "../lib/api";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Search, ListTodo, ChevronRight, CheckCircle2, Clock, Calendar, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import TaskStageBadge from "../components/TaskStageBadge";

// Generate month options for filter
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = -6; i <= 6; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const label = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    options.push({ value, label });
  }
  return options;
};

const TasksPage = () => {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("active");
  const [completedMonthFilter, setCompletedMonthFilter] = useState("all");
  
  // New filters
  const [pmFilter, setPmFilter] = useState("all");
  const [artistFilter, setArtistFilter] = useState("all");
  const [clientFilter, setClientFilter] = useState("all");
  const [projectFilter, setProjectFilter] = useState("all");
  const [specFilter, setSpecFilter] = useState("all");
  const [serviceTypes, setServiceTypes] = useState([]);
  const [sortField, setSortField] = useState("deadline");
  const [sortDirection, setSortDirection] = useState("asc");
  
  const isArtDirector = user?.role === "art_director";

  const SPEC_GROUPS = {
    interior: { label: "Interior", match: (t) => /interior|360.*room|360.*apartment|360.*sphere/i.test(t) },
    exterior: { label: "Exterior", match: (t) => /exterior|eye.level|aerial/i.test(t) },
    animation: { label: "Animation", match: (t) => /animation|spinhouse/i.test(t) },
    plans: { label: "Plans / Modeling", match: (t) => /floor.plan|model/i.test(t) },
    other: { label: "Other", match: (t) => /other|website|360.*virtual.*large/i.test(t) },
  };

  const monthOptions = useMemo(() => getMonthOptions(), []);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const sortTasks = (taskList) => {
    return [...taskList].sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      let valA, valB;
      switch (sortField) {
        case "name":
          return dir * (a.name || "").localeCompare(b.name || "");
        case "project":
          return dir * (a.project_name || "").localeCompare(b.project_name || "");
        case "type":
          return dir * (a.task_type || "").localeCompare(b.task_type || "");
        case "artist":
          return dir * (a.assigned_artist_name || "\uffff").localeCompare(b.assigned_artist_name || "\uffff");
        case "deadline":
          valA = a.deadline ? new Date(a.deadline).getTime() : (dir === 1 ? Infinity : -Infinity);
          valB = b.deadline ? new Date(b.deadline).getTime() : (dir === 1 ? Infinity : -Infinity);
          return dir * (valA - valB);
        case "completed_at":
          valA = (a.completed_at || a.updated_at) ? new Date(a.completed_at || a.updated_at).getTime() : 0;
          valB = (b.completed_at || b.updated_at) ? new Date(b.completed_at || b.updated_at).getTime() : 0;
          return dir * (valA - valB);
        case "artist_cost":
          return dir * ((a.agreed_price || 0) - (b.agreed_price || 0));
        case "ad_fee":
          return dir * ((a.art_director_fee || 0) - (b.art_director_fee || 0));
        case "client_price":
          return dir * ((a.client_price || 0) - (b.client_price || 0));
        case "profit":
          valA = (a.client_price || 0) - (a.agreed_price || 0);
          valB = (b.client_price || 0) - (b.agreed_price || 0);
          return dir * (valA - valB);
        case "margin":
          valA = a.client_price > 0 ? ((a.client_price - (a.agreed_price || 0)) / a.client_price) : -Infinity;
          valB = b.client_price > 0 ? ((b.client_price - (b.agreed_price || 0)) / b.client_price) : -Infinity;
          return dir * (valA - valB);
        case "stage":
          return dir * taskStageName(a).localeCompare(taskStageName(b));
        default:
          return 0;
      }
    });
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortDirection === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1 text-orange-400" />
      : <ArrowDown className="h-3 w-3 ml-1 text-orange-400" />;
  };

  useEffect(() => {
    fetchTasks();
    fetchServiceTypes();
  }, []);

  const fetchServiceTypes = async () => {
    try {
      const res = await api.get("/service-types/list");
      setServiceTypes(res.data || []);
    } catch (err) {
      console.error("Failed to load service types", err);
    }
  };

  const fetchTasks = async () => {
    try {
      // Fetch all tasks including completed
      const response = await tasksApi.getAll({});
      setTasks(response.data);
    } catch (error) {
      console.error("Error fetching tasks:", error);
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  };

  // Extract unique PMs, Artists, and Clients for filters
  const filterOptions = useMemo(() => {
    const pms = new Map();
    const artists = new Map();
    const clients = new Map();
    const projects = new Map();
    
    tasks.forEach(t => {
      if (t.project_manager_id && t.project_manager_name) {
        pms.set(t.project_manager_id, t.project_manager_name);
      }
      if (t.assigned_artist_id && t.assigned_artist_name) {
        artists.set(t.assigned_artist_id, t.assigned_artist_name);
      }
      if (t.client_id && t.client_name) {
        clients.set(t.client_id, t.client_name);
      }
      if (t.project_id && t.project_name) {
        projects.set(t.project_id, t.project_name);
      }
    });
    
    return {
      pms: Array.from(pms, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      artists: Array.from(artists, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      clients: Array.from(clients, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
      projects: Array.from(projects, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)),
    };
  }, [tasks]);

  const getTypeBadge = (type) => {
    const labels = {
      exterior_render: "Exterior",
      interior_render: "Interior",
      animation: "Animation",
      floor_plan: "Floor Plan",
      vr: "VR",
      other: "Other",
    };
    return (
      <span className="text-xs text-zinc-500 font-mono uppercase">
        {labels[type] || type}
      </span>
    );
  };

  const getDeadlineDisplay = (deadline) => {
    if (!deadline) return <span className="text-zinc-600">—</span>;
    
    const date = new Date(deadline);
    const now = new Date();
    const diffDays = Math.ceil((date - now) / (1000 * 60 * 60 * 24));
    
    let className = "text-zinc-400";
    let label = date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
    
    if (diffDays < 0) {
      className = "text-red-400";
      label = `${Math.abs(diffDays)}d overdue`;
    } else if (diffDays <= 3) {
      className = "text-orange-400";
      label = `${diffDays}d left`;
    } else if (diffDays <= 7) {
      className = "text-yellow-400";
      label = `${diffDays}d left`;
    }
    
    return <span className={cn("text-xs font-mono", className)}>{label}</span>;
  };

  // Split tasks into active and completed
  const activeTasks = useMemo(() => {
    return tasks.filter(t => !isTaskDone(t));
  }, [tasks]);

  const completedTasks = useMemo(() => {
    return tasks.filter(t => isTaskDone(t));
  }, [tasks]);

  // Stage names of the open tasks — every service names its own stages.
  const stageOptions = useMemo(
    () => [...new Set(activeTasks.map(taskStageName).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [activeTasks]
  );

  // Filter active tasks with new filters and sort by deadline
  const filteredActiveTasks = useMemo(() => {
    const filtered = activeTasks.filter((t) => {
      const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.assigned_artist_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === "all" || t.task_type === typeFilter;
      const matchesStage = stageFilter === "all" || taskStageName(t) === stageFilter;
      const matchesPm = pmFilter === "all" || t.project_manager_id === pmFilter;
      const matchesArtist = artistFilter === "all" || t.assigned_artist_id === artistFilter;
      const matchesClient = clientFilter === "all" || t.client_id === clientFilter;
      const matchesProject = projectFilter === "all" || t.project_id === projectFilter;
      const matchesSpec = specFilter === "all" || (SPEC_GROUPS[specFilter] && SPEC_GROUPS[specFilter].match(t.task_type || ""));
      return matchesSearch && matchesType && matchesStage && matchesPm && matchesArtist && matchesClient && matchesProject && matchesSpec;
    });
    
    return sortTasks(filtered);
  }, [activeTasks, searchQuery, typeFilter, stageFilter, pmFilter, artistFilter, clientFilter, projectFilter, specFilter, sortField, sortDirection]);

  // Filter completed tasks with month filter and new filters
  const filteredCompletedTasks = useMemo(() => {
    const filtered = completedTasks.filter((t) => {
      const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.project_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.assigned_artist_name?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === "all" || t.task_type === typeFilter;
      const matchesPm = pmFilter === "all" || t.project_manager_id === pmFilter;
      const matchesArtist = artistFilter === "all" || t.assigned_artist_id === artistFilter;
      const matchesClient = clientFilter === "all" || t.client_id === clientFilter;
      const matchesProject = projectFilter === "all" || t.project_id === projectFilter;
      const matchesSpec = specFilter === "all" || (SPEC_GROUPS[specFilter] && SPEC_GROUPS[specFilter].match(t.task_type || ""));
      
      // Month filter based on completed_at or updated_at
      let matchesMonth = true;
      if (completedMonthFilter !== "all") {
        const completedDate = t.completed_at || t.updated_at;
        if (completedDate) {
          const taskMonth = completedDate.substring(0, 7); // "YYYY-MM"
          matchesMonth = taskMonth === completedMonthFilter;
        }
      }
      
      return matchesSearch && matchesType && matchesMonth && matchesPm && matchesArtist && matchesClient && matchesProject && matchesSpec;
    });
    
    return sortTasks(filtered);
  }, [completedTasks, searchQuery, typeFilter, completedMonthFilter, pmFilter, artistFilter, clientFilter, projectFilter, specFilter, sortField, sortDirection]);

  // Render task row helper function (not a component)
  const renderTaskRow = (task) => {
    const clientPrice = task.client_price || 0;
    const artistPrice = task.agreed_price || 0;
    const adFee = task.art_director_fee || 0;
    const adPercent = adFee > 0 && artistPrice > 0 ? (adFee / artistPrice) * 100 : 0;
    const hasAssignedArtist = !!task.assigned_artist_id;
    const hasCosts = artistPrice > 0;
    const profit = clientPrice - artistPrice;
    const marginPercent = clientPrice > 0 && hasCosts ? (profit / clientPrice) * 100 : 0;
    const marginColor = !hasCosts && clientPrice > 0 ? "text-amber-400" :
      marginPercent >= 50 ? "text-green-400" : 
      marginPercent >= 30 ? "text-yellow-400" : 
      marginPercent > 0 ? "text-red-400" : "text-zinc-500";
    const isZeroMargin = hasCosts && marginPercent <= 0;
    const showFinancials = user?.role !== "client" && !isArtDirector;

    return (
      <TableRow
        key={task.id}
        className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer group"
        data-testid={`task-row-${task.id}`}
      >
        <TableCell>
          <Link to={`/tasks/${task.id}`}>
            <p className="text-zinc-50 font-medium group-hover:text-orange-500 transition-colors text-sm">
              {task.name}
            </p>
          </Link>
        </TableCell>
        <TableCell>
          <span className="text-zinc-400 text-sm">{task.project_name}</span>
        </TableCell>
        <TableCell>
          {getTypeBadge(task.task_type)}
        </TableCell>
        <TableCell>
          {task.assigned_artist_name ? (
            <div className="flex items-center gap-1.5">
              <div className="w-5 h-5 rounded-full bg-zinc-800 flex items-center justify-center">
                <span className="text-xs text-zinc-400">{task.assigned_artist_name.charAt(0)}</span>
              </div>
              <span className="text-zinc-300 text-sm">{task.assigned_artist_name}</span>
            </div>
          ) : (
            <span className="text-orange-400 text-sm">Unassigned</span>
          )}
        </TableCell>
        <TableCell>
          {isTaskDone(task) ? (
            <span className="text-xs text-zinc-500 font-mono">
              {(task.completed_at || task.stage_changed_at) ? new Date(task.completed_at || task.stage_changed_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) : "—"}
            </span>
          ) : (
            getDeadlineDisplay(task.deadline)
          )}
        </TableCell>
        {/* AD view: show only artist cost + AD fee */}
        {isArtDirector && (
          <>
            <TableCell className="text-right font-mono text-sm">
              {artistPrice > 0 ? (
                <span className="text-zinc-400">{formatAmount(artistPrice, task.currency || "USD")}</span>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {adFee > 0 ? (
                <span className="text-purple-400">{formatAmount(adFee, task.currency || "USD")}</span>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </TableCell>
          </>
        )}
        {/* Full financial view: client/artist/profit/margin */}
        {showFinancials && (
          <>
            <TableCell className="text-right font-mono text-sm text-zinc-300">
              {clientPrice > 0 ? formatAmount(clientPrice, task.currency || "USD") : <span className="text-zinc-600">—</span>}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {!hasAssignedArtist && artistPrice > 0 ? (
                <span className="text-orange-400" title="Artist not assigned">{formatAmount(artistPrice, task.currency || "USD")}*</span>
              ) : artistPrice > 0 ? (
                <span className="text-zinc-400">{formatAmount(artistPrice, task.currency || "USD")}</span>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </TableCell>
            <TableCell className="text-right font-mono text-sm">
              {!hasAssignedArtist ? (
                <span className="text-zinc-600">—</span>
              ) : profit > 0 ? (
                <span className="text-green-400">{formatAmount(profit, task.currency || "USD")}</span>
              ) : profit < 0 ? (
                <span className="text-red-400">{formatAmount(profit, task.currency || "USD")}</span>
              ) : (
                <span className="text-zinc-600">—</span>
              )}
            </TableCell>
            <TableCell className="text-right">
              {!hasAssignedArtist ? (
                <span className="text-orange-400 text-xs">No artist</span>
              ) : !hasCosts && clientPrice > 0 ? (
                <span className="text-amber-400 text-xs font-medium">— (add costs)</span>
              ) : isZeroMargin ? (
                <span className="text-red-400 font-mono text-sm font-medium">0%!</span>
              ) : clientPrice > 0 && hasCosts ? (
                <span className={cn("font-mono text-sm font-medium", marginColor)}>
                  {marginPercent.toFixed(0)}%
                </span>
              ) : (
                <span className="text-zinc-600 text-sm">—</span>
              )}
            </TableCell>
          </>
        )}
        {/* AD fee % column for admin/PM view */}
        {showFinancials && (
          <TableCell className="text-right font-mono text-sm">
            {adPercent > 0 ? (
              <span className="text-purple-400">{adPercent.toFixed(0)}%</span>
            ) : (
              <span className="text-zinc-600">—</span>
            )}
          </TableCell>
        )}
        <TableCell className="text-center">
          <TaskStageBadge task={task} size="sm" />
        </TableCell>
        <TableCell>
          <Link to={`/tasks/${task.id}`}>
            <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-orange-500 transition-colors" />
          </Link>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Tasks</h1>
          <p className="text-sm text-zinc-500">
            {activeTasks.length} active • {completedTasks.length} completed
          </p>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSortField(v === "completed" ? "completed_at" : "deadline"); setSortDirection(v === "completed" ? "desc" : "asc"); }} className="space-y-4">
        <TabsList className="bg-zinc-900 border border-zinc-800">
          <TabsTrigger value="active" className="data-[state=active]:bg-zinc-800">
            <Clock className="h-4 w-4 mr-2" />
            Active Tasks ({activeTasks.length})
          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-zinc-800">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Completed ({completedTasks.length})
          </TabsTrigger>
        </TabsList>

        {/* Active Tasks Tab */}
        <TabsContent value="active" className="space-y-4">
          {/* All Filters in One Row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-48">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 h-8 text-sm"
                data-testid="search-tasks"
              />
            </div>
            <Select value={stageFilter} onValueChange={setStageFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="stage-filter">
                <SelectValue placeholder="Stage" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-300">All stages</SelectItem>
                {stageOptions.map((name) => (
                  <SelectItem key={name} value={name} className="text-zinc-300">
                    {name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Types</SelectItem>
                {serviceTypes.map(st => (
                  <SelectItem key={st.id || st.name} value={st.name} className="text-zinc-300">
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pmFilter} onValueChange={setPmFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="pm-filter">
                <SelectValue placeholder="PM" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All PMs</SelectItem>
                {filterOptions.pms.map((pm) => (
                  <SelectItem key={pm.id} value={pm.id} className="text-zinc-300">
                    {pm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={artistFilter} onValueChange={setArtistFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="artist-filter">
                <SelectValue placeholder="Artist" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Artists</SelectItem>
                {filterOptions.artists.map((artist) => (
                  <SelectItem key={artist.id} value={artist.id} className="text-zinc-300">
                    {artist.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="client-filter">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Clients</SelectItem>
                {filterOptions.clients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="text-zinc-300">
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="project-filter">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Projects</SelectItem>
                {filterOptions.projects.map((project) => (
                  <SelectItem key={project.id} value={project.id} className="text-zinc-300">
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={specFilter} onValueChange={setSpecFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="spec-filter">
                <SelectValue placeholder="Specialization" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-300">All Specs</SelectItem>
                {Object.entries(SPEC_GROUPS).map(([key, group]) => (
                  <SelectItem key={key} value={key} className="text-zinc-300">
                    {group.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Reset filters button */}
            {(pmFilter !== "all" || artistFilter !== "all" || clientFilter !== "all" || stageFilter !== "all" || typeFilter !== "all" || projectFilter !== "all" || specFilter !== "all" || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPmFilter("all");
                  setArtistFilter("all");
                  setClientFilter("all");
                  setStageFilter("all");
                  setTypeFilter("all");
                  setProjectFilter("all");
                  setSpecFilter("all");
                  setSearchQuery("");
                }}
                className="text-zinc-400 hover:text-zinc-50 h-8 text-sm px-2"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Active Tasks Table */}
          {filteredActiveTasks.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <ListTodo className="h-10 w-10 text-zinc-600 mb-3" />
                <p className="text-zinc-500">No active tasks found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("name")}>
                      <div className="flex items-center">Task<SortIcon field="name" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("project")}>
                      <div className="flex items-center">Project<SortIcon field="project" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("type")}>
                      <div className="flex items-center">Type<SortIcon field="type" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist")}>
                      <div className="flex items-center">Artist<SortIcon field="artist" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("deadline")}>
                      <div className="flex items-center">Deadline<SortIcon field="deadline" /></div>
                    </TableHead>
                    {isArtDirector && (
                      <>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist_cost")}>
                          <div className="flex items-center justify-end">Artist Cost<SortIcon field="artist_cost" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("ad_fee")}>
                          <div className="flex items-center justify-end">AD Fee<SortIcon field="ad_fee" /></div>
                        </TableHead>
                      </>
                    )}
                    {!isArtDirector && user?.role !== "client" && (
                      <>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("client_price")}>
                          <div className="flex items-center justify-end">Client<SortIcon field="client_price" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist_cost")}>
                          <div className="flex items-center justify-end">Artist<SortIcon field="artist_cost" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("profit")}>
                          <div className="flex items-center justify-end">Profit<SortIcon field="profit" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("margin")}>
                          <div className="flex items-center justify-end">Margin<SortIcon field="margin" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("ad_fee")}>
                          <div className="flex items-center justify-end">AD%<SortIcon field="ad_fee" /></div>
                        </TableHead>
                      </>
                    )}
                    <TableHead className="text-zinc-400 font-medium text-center cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("stage")}>
                      <div className="flex items-center justify-center">Stage<SortIcon field="stage" /></div>
                    </TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredActiveTasks.map((task) => renderTaskRow(task))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>

        {/* Completed Tasks Tab */}
        <TabsContent value="completed" className="space-y-4">
          {/* All Filters in One Row for Completed */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative w-48">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 h-8 text-sm"
              />
            </div>
            <Select value={completedMonthFilter} onValueChange={setCompletedMonthFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-300">All Months</SelectItem>
                {monthOptions.map(m => (
                  <SelectItem key={m.value} value={m.value} className="text-zinc-300">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Types</SelectItem>
                {serviceTypes.map(st => (
                  <SelectItem key={st.id || st.name} value={st.name} className="text-zinc-300">
                    {st.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={pmFilter} onValueChange={setPmFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="PM" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All PMs</SelectItem>
                {filterOptions.pms.map((pm) => (
                  <SelectItem key={pm.id} value={pm.id} className="text-zinc-300">
                    {pm.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={artistFilter} onValueChange={setArtistFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Artist" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Artists</SelectItem>
                {filterOptions.artists.map((artist) => (
                  <SelectItem key={artist.id} value={artist.id} className="text-zinc-300">
                    {artist.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={clientFilter} onValueChange={setClientFilter}>
              <SelectTrigger className="w-32 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Clients</SelectItem>
                {filterOptions.clients.map((client) => (
                  <SelectItem key={client.id} value={client.id} className="text-zinc-300">
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm">
                <SelectValue placeholder="Project" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                <SelectItem value="all" className="text-zinc-300">All Projects</SelectItem>
                {filterOptions.projects.map((project) => (
                  <SelectItem key={project.id} value={project.id} className="text-zinc-300">
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={specFilter} onValueChange={setSpecFilter}>
              <SelectTrigger className="w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-8 text-sm" data-testid="spec-filter-completed">
                <SelectValue placeholder="Specialization" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="text-zinc-300">All Specs</SelectItem>
                {Object.entries(SPEC_GROUPS).map(([key, group]) => (
                  <SelectItem key={key} value={key} className="text-zinc-300">
                    {group.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {(pmFilter !== "all" || artistFilter !== "all" || clientFilter !== "all" || typeFilter !== "all" || completedMonthFilter !== "all" || projectFilter !== "all" || specFilter !== "all" || searchQuery) && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setPmFilter("all");
                  setArtistFilter("all");
                  setClientFilter("all");
                  setTypeFilter("all");
                  setProjectFilter("all");
                  setSpecFilter("all");
                  setCompletedMonthFilter("all");
                  setSearchQuery("");
                }}
                className="text-zinc-400 hover:text-zinc-50 h-8 text-sm px-2"
              >
                Clear
              </Button>
            )}
          </div>

          {/* Completed Tasks Table */}
          {filteredCompletedTasks.length === 0 ? (
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <CheckCircle2 className="h-10 w-10 text-zinc-600 mb-3" />
                <p className="text-zinc-500">No completed tasks found</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800 hover:bg-transparent">
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("name")}>
                      <div className="flex items-center">Task<SortIcon field="name" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("project")}>
                      <div className="flex items-center">Project<SortIcon field="project" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("type")}>
                      <div className="flex items-center">Type<SortIcon field="type" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist")}>
                      <div className="flex items-center">Artist<SortIcon field="artist" /></div>
                    </TableHead>
                    <TableHead className="text-zinc-400 font-medium cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("completed_at")}>
                      <div className="flex items-center">Completed<SortIcon field="completed_at" /></div>
                    </TableHead>
                    {isArtDirector && (
                      <>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist_cost")}>
                          <div className="flex items-center justify-end">Artist Cost<SortIcon field="artist_cost" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("ad_fee")}>
                          <div className="flex items-center justify-end">AD Fee<SortIcon field="ad_fee" /></div>
                        </TableHead>
                      </>
                    )}
                    {!isArtDirector && user?.role !== "client" && (
                      <>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("client_price")}>
                          <div className="flex items-center justify-end">Client<SortIcon field="client_price" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("artist_cost")}>
                          <div className="flex items-center justify-end">Artist<SortIcon field="artist_cost" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("profit")}>
                          <div className="flex items-center justify-end">Profit<SortIcon field="profit" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("margin")}>
                          <div className="flex items-center justify-end">Margin<SortIcon field="margin" /></div>
                        </TableHead>
                        <TableHead className="text-zinc-400 font-medium text-right cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("ad_fee")}>
                          <div className="flex items-center justify-end">AD%<SortIcon field="ad_fee" /></div>
                        </TableHead>
                      </>
                    )}
                    <TableHead className="text-zinc-400 font-medium text-center cursor-pointer select-none hover:text-zinc-200" onClick={() => handleSort("stage")}>
                      <div className="flex items-center justify-center">Stage<SortIcon field="stage" /></div>
                    </TableHead>
                    <TableHead className="w-8"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCompletedTasks.map((task) => renderTaskRow(task))}
                </TableBody>
              </Table>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TasksPage;
